import { useDubbingStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import ManageAiVoicesModal from './ManageAiVoicesModal';
import { useSpeakerAiVoices } from './useSpeakerAiVoices';
import VoiceAvatar from './VoiceAvatar';
import VoiceInfoCard from './VoiceInfoCard';
import { voiceMeta } from './voiceLabels';
import { resolveLanguageDefault } from './voiceLanguageDefaults';
import { DefaultBadge, EmptyState } from './voiceMapperStyles';

import type { AiVoiceRecord } from '@src/types.ts/aiVoiceRecord';

interface SpeakerAiVoicesInfoTabProps {
  speakerId: string;
  speakerName: string;
  speakerHeadshotUrl?: string | null;
}

// View-only "AI Voices" tab inside the speaker pop-over (DE4). A tight table of
// the speaker's voices (top = default) sits above a card for the selected voice
// where you sample it per language (PLAT-10502). Languages this voice is the
// speaker's default for are badged "Default". Editing lives in the Manage AI
// Voices modal, reachable via "Manage voices".
const SpeakerAiVoicesInfoTab = observer(
  ({
    speakerId,
    speakerName,
    speakerHeadshotUrl,
  }: SpeakerAiVoicesInfoTabProps) => {
    const dubbingStore = useDubbingStore();
    const videoCreator = useVideoCreatorStore();
    const { aiVoiceIds, languagePrefs, loading, reload } =
      useSpeakerAiVoices(speakerId);
    const [managing, setManaging] = useState(false);
    const [selectedVoiceId, setSelectedVoiceId] = useState('');

    // The speaker's ordered voices, resolved to catalog records (top = default).
    const orderedVoices = useMemo(
      () =>
        aiVoiceIds
          .map(id => dubbingStore.voiceById(id))
          .filter((v): v is AiVoiceRecord => Boolean(v)),
      [aiVoiceIds, dubbingStore, dubbingStore.catalog],
    );

    // Keep the sampled voice valid as the list loads / changes.
    useEffect(() => {
      if (
        orderedVoices.length > 0 &&
        !orderedVoices.some(v => v.id === selectedVoiceId)
      ) {
        setSelectedVoiceId(orderedVoices[0].id);
      }
    }, [orderedVoices, selectedVoiceId]);

    const selectedVoice =
      orderedVoices.find(v => v.id === selectedVoiceId) ?? null;
    const hasVoices = orderedVoices.length > 0;

    const languages = useMemo(
      () => videoCreator.organization?.supportedLanguages ?? [],
      [videoCreator.organization?.supportedLanguages],
    );

    // The workspace languages the selected voice is the speaker's default for:
    // per language, the resolved default is the pinned voice (if it speaks it)
    // else the first voice in order that speaks it — matching the Manage modal.
    const defaultForLanguageValues = useMemo(() => {
      const values = new Set<string>();
      if (!selectedVoice) return values;
      for (const language of languages) {
        const { resolvedVoiceId } = resolveLanguageDefault(
          orderedVoices,
          languagePrefs,
          language,
        );
        if (resolvedVoiceId === selectedVoice.id) values.add(language.value);
      }
      return values;
    }, [selectedVoice, languages, orderedVoices, languagePrefs]);

    return (
      <Container>
        <HeaderRow>
          <span className="title">Voices For This Speaker</span>
          <ManageLink type="button" onClick={() => setManaging(true)}>
            Manage voices →
          </ManageLink>
        </HeaderRow>

        {loading ? (
          <Muted>Loading…</Muted>
        ) : !hasVoices ? (
          <EmptyState>
            No AI Voices assigned yet. Use “Manage voices” to add some.
          </EmptyState>
        ) : (
          <>
            <VoiceListBox>
              {orderedVoices.map((voice, index) => (
                <VoiceRow
                  key={voice.id}
                  type="button"
                  $selected={voice.id === selectedVoiceId}
                  onClick={() => setSelectedVoiceId(voice.id)}
                >
                  <VoiceAvatar voice={voice} />
                  <span className="labels">
                    <span className="name">
                      {voice.name}
                      {index === 0 && <DefaultBadge>Default</DefaultBadge>}
                    </span>
                    <span className="meta">
                      {voiceMeta(voice.provider, voice.gender)}
                    </span>
                  </span>
                </VoiceRow>
              ))}
            </VoiceListBox>

            {selectedVoice && (
              <CardCell>
                <VoiceInfoCard
                  voice={selectedVoice}
                  defaultForLanguageValues={defaultForLanguageValues}
                />
              </CardCell>
            )}
          </>
        )}

        {managing && (
          <ManageAiVoicesModal
            isOpen
            onClose={() => {
              setManaging(false);
              void reload();
            }}
            speakerId={speakerId}
            speakerName={speakerName}
            speakerHeadshotUrl={speakerHeadshotUrl}
          />
        )}
      </Container>
    );
  },
);

export default SpeakerAiVoicesInfoTab;

// Bounded to the same height as the Info form so switching tabs doesn't resize
// the modal (or push it off screen). The voice table keeps its (capped) height
// and the card below fills the remainder, scrolling its language list inside.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 600px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;

  .title {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-medium);
  }
`;

// A compact, bordered table of voice rows — same treatment as the card's
// language list. Shows ~3 rows, then scrolls, rather than a stack of cards.
const VoiceListBox = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: 8px;
`;

// The selected voice's card sits BELOW the table and fills the leftover height.
// VoiceInfoCard is absolutely positioned on wide viewports, so this needs to be
// a positioned cell with a definite height; its language list scrolls inside.
const CardCell = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

const VoiceRow = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  text-align: left;
  background: ${p =>
    p.$selected
      ? 'color-mix(in srgb, var(--primary) 12%, transparent)'
      : 'transparent'};
  transition: var(--transition-normal);

  &:last-child {
    border-bottom: none;
  }
  &:hover {
    background: var(--surface-elevated);
  }

  .labels {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .name {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--foreground);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semibold);
  }
  .meta {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;

const Muted = styled.span`
  color: var(--text-muted);
  font-size: var(--text-sm);
`;

const ManageLink = styled.button`
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  color: var(--primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;
